import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import Character from '../../../models/Game Layer/character';
import Player from '../../../models/Game Layer/player';
import Session from '../../../models/Game Layer/session';
import User from '../../../models/user';
import { GamelayerService } from '../../../services/gamelayer-service';
import { UserService } from '../../../services/user-service';
import { BackButtonComponent } from '../../back-button-component/back-button-component';
import { LoadingComponent } from '../../loading-component/loading-component';

type CampaignPlayerEntry = {
  player: Player;
  character: Character | null;
};

type CampaignSessionRoster = {
  session: Session;
  players: CampaignPlayerEntry[];
};

@Component({
  selector: 'app-campaign-info-component',
  standalone: true,
  imports: [CommonModule, RouterLink, BackButtonComponent, LoadingComponent],
  templateUrl: './campaign-info-component.html',
  styleUrl: './campaign-info-component.css',
})
export class CampaignInfoComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly gamelayerService = inject(GamelayerService);
  private readonly userService = inject(UserService);

  readonly campaignId = signal('');
  readonly campaignName = signal('');
  readonly selectedSessionId = signal('');
  readonly users = signal<User[]>([]);
  readonly sessionRosters = signal<CampaignSessionRoster[]>([]);
  readonly isLoadingCampaign = signal(true);
  readonly loadCampaignError = signal('');

  readonly totalPlayers = computed(() =>
    this.sessionRosters().reduce((total, roster) => total + roster.players.length, 0),
  );

  ngOnInit(): void {
    const campaignId = this.route.snapshot.paramMap.get('campaignId') ?? '';
    const campaignName = this.route.snapshot.queryParamMap.get('campaignName') ?? '';
    const selectedSessionId = this.route.snapshot.queryParamMap.get('sessionId') ?? '';

    this.campaignId.set(campaignId);
    this.campaignName.set(campaignName);
    this.selectedSessionId.set(selectedSessionId);
    this.loadCampaign();
  }

  private loadCampaign(): void {
    if (!this.campaignId()) {
      this.loadCampaignError.set('No campaign was provided for this page.');
      this.isLoadingCampaign.set(false);
      return;
    }

    this.isLoadingCampaign.set(true);
    this.loadCampaignError.set('');

    forkJoin({
      sessionsResponse: this.gamelayerService.getSessions(),
      usersResponse: this.userService.getUsers(),
    }).subscribe({
      next: ({ sessionsResponse, usersResponse }) => {
        const rawUsers = Array.isArray(usersResponse)
          ? usersResponse
          : (usersResponse as { users?: unknown[] })?.users ?? [];

        this.users.set(rawUsers.map((user) => new User(user)));

        const rawSessions = Array.isArray(sessionsResponse)
          ? sessionsResponse
          : (sessionsResponse as { sessions?: unknown[] })?.sessions ?? [];
        const sessions = rawSessions.map((session) => new Session(session));
        const matchingSessions = sessions.filter((session) => {
          const matchesCampaign = session.campaignId === this.campaignId();
          const selectedSessionId = this.selectedSessionId();

          if (!selectedSessionId) {
            return matchesCampaign;
          }

          return matchesCampaign && session.id === selectedSessionId;
        });

        if (!matchingSessions.length) {
          this.sessionRosters.set([]);
          this.isLoadingCampaign.set(false);
          return;
        }

        const characterRequests = matchingSessions.map((session) =>
          this.gamelayerService.getCharacters(session.id),
        );

        forkJoin(characterRequests).subscribe({
          next: (charactersBySession) => {
            const rosters = matchingSessions.map((session, index) => {
              const characters = (charactersBySession[index] ?? []).map(
                (character) => new Character(character),
              );
              const players = session.players.map((player) => ({
                player,
                character:
                  characters.find((character) => character.id === player.characterId) ?? null,
              }));

              return {
                session,
                players,
              };
            });

            if (!this.campaignName() && rosters[0]?.session.campaignName) {
              this.campaignName.set(rosters[0].session.campaignName);
            }

            this.sessionRosters.set(rosters);
            this.isLoadingCampaign.set(false);
          },
          error: (error) => {
            console.error('Error loading campaign characters:', error);
            this.loadCampaignError.set('The campaign roster could not be loaded right now.');
            this.isLoadingCampaign.set(false);
          },
        });
      },
      error: (error) => {
        console.error('Error loading campaign records:', error);
        this.loadCampaignError.set('The campaign details could not be loaded right now.');
        this.isLoadingCampaign.set(false);
      },
    });
  }

  getUserName(userId: string): string {
    return this.users().find((user) => user.id === userId)?.name || 'Unknown User';
  }
}
